import React from 'react';
import { Row, Col } from 'antd';
import {
  getFlightDetailsByAircraftId,
  getSelectedFlightAircraftId
} from '../../redux/selectors/flyingPage';
import { useSelector } from 'react-redux';
import { StatusV2 } from './AircraftStatusV2';
import { getDateFormat, getTimezone } from '../../redux/selectors/userData';
import { formatDisplayTime, formatDuration } from './utils/timeUtils';
import { SOS } from '../Flying/AircraftList/constants';
import { AMBER_6, RED } from './styles/constants';
import { TrackPoint } from '../../common/api/spidertracks-sdk/types/TrackData';
import { convertKnotsTo, convertToMetres } from '../Flying/Map/utils/helper';
import { CollapsedFlightDetailHeader } from './CollapsedFlightDetailHeader';

const SHOW_AGL_THRESHOLD_METERS = 304.8; // 1000 feet
const FIFTEEN_MINUTES = 15 * 60 * 1000;
export const ONE_HOUR = 60 * 60 * 1000;
const SPEED_THRESHOLD_KNOTS = 30;
const minHeight = '48px';
interface CollapsedFlightDetailItemProps {
  aircraftId: string;
}

function getAltitude(latestPoint: TrackPoint): { value: number; type: 'AMSL' | 'AGL' } {
  if (latestPoint.altitudeAgl === undefined) {
    return {
      value: Math.round(latestPoint.altitude),
      type: 'AMSL'
    };
  }
  const altitudeAglMeters = convertToMetres(latestPoint.altitudeAgl, latestPoint?.altitudeUnit)[0];
  if (altitudeAglMeters < SHOW_AGL_THRESHOLD_METERS) {
    return {
      value: Math.round(latestPoint.altitudeAgl),
      type: 'AGL'
    };
  } else {
    return {
      value: Math.round(latestPoint.altitude),
      type: 'AMSL'
    };
  }
}

function isFlightActive(latestPointTime: number): boolean {
  if (Date.now() - latestPointTime >= FIFTEEN_MINUTES) {
    return false;
  }
  return true;
}

function isSpeedOverThreshold(point: TrackPoint): boolean {
  return convertKnotsTo(point.speed, point.speedUnit) > SPEED_THRESHOLD_KNOTS;
}

const UnitWrapper: React.FC<{ unit: string; type: string | undefined; inheritColour: boolean }> = ({
  unit,
  type,
  inheritColour
}) => {
  return (
    <span style={{ color: inheritColour ? 'inherit' : '#8C8C8C' }}>
      {unit} <span style={{ fontSize: 'xx-small' }}>{type}</span>
    </span>
  );
};

const CollapsedFlightDetailItem: React.FC<CollapsedFlightDetailItemProps> = ({ aircraftId }) => {
  const selectedAircraftId = useSelector(getSelectedFlightAircraftId);
  const flightDetails = useSelector(getFlightDetailsByAircraftId(aircraftId));
  const dateFormat = useSelector(getDateFormat);
  const timeFormat = 'HH:mm';
  const dateTimeFormat = `${dateFormat} ${timeFormat}`;
  const timezone = useSelector(getTimezone);
  const activeSOS = flightDetails?.aircraft.trackStatus === SOS;

  if (!flightDetails) {
    return <></>;
  }
  const latestPoint = flightDetails.track.points[flightDetails.track.points.length - 1];
  if (!latestPoint) {
    return <></>;
  }
  const formattedDepartedTime = formatDisplayTime(
    flightDetails.track.departedTime,
    timezone,
    dateTimeFormat,
    timeFormat
  );
  const formattedEndTime = formatDisplayTime(
    flightDetails.track.endTime,
    timezone,
    dateTimeFormat,
    timeFormat
  );
  const flightDuration = formatDuration(
    flightDetails.track.departedTime,
    flightDetails.track.endTime
  );
  const flightActive = isFlightActive(latestPoint.timestamp);
  const lastKnownSpeedOverThreshold = isSpeedOverThreshold(latestPoint) && !flightActive;

  return (
    <Row
      className={`flight-detail-list-item ${
        selectedAircraftId === aircraftId
          ? activeSOS
            ? 'selected-list-item-sos'
            : 'selected-list-item'
          : ''
      }`}
      //Required for the StatusV2 component to be vertically centered
      style={{ minHeight, color: activeSOS ? RED : undefined }}
    >
      <Col span={3}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-around',
            height: minHeight,
            paddingRight: '7px'
          }}
        >
          <StatusV2 aircraftId={aircraftId} />
        </div>
      </Col>
      <Col span={21}>
        <CollapsedFlightDetailHeader flightDetails={flightDetails} flightActive={flightActive} />
        <Row>
          <Col span={flightActive ? 4 : 12} style={{ textAlign: 'left' }}>
            {flightActive ? (
              <span>{formattedDepartedTime}</span>
            ) : (
              <span>
                {formattedDepartedTime} - {formattedEndTime}
              </span>
            )}
          </Col>
          <Col span={flightActive ? 8 : 0} style={{ textAlign: 'end' }}>
            <span>
              {getAltitude(latestPoint).value}{' '}
              <UnitWrapper
                unit={`${latestPoint.altitudeUnit}`}
                type={getAltitude(latestPoint).type}
                inheritColour={activeSOS}
              />
            </span>
          </Col>
          <Col span={6} style={{ textAlign: 'end' }}>
            {flightActive || lastKnownSpeedOverThreshold ? (
              <span
                style={{
                  color: lastKnownSpeedOverThreshold ? AMBER_6 : undefined
                }}
              >
                {Math.round(latestPoint.speed)}{' '}
                <UnitWrapper
                  unit={latestPoint.speedUnit}
                  inheritColour={lastKnownSpeedOverThreshold || activeSOS}
                  type={undefined}
                />
              </span>
            ) : (
              <></>
            )}
          </Col>
          <Col
            style={{
              textAlign: 'end',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'flex-end',
              paddingLeft: '3px'
            }}
          >
            <div style={{ width: '50px', textAlign: 'right', marginRight: '4px' }}>
              <span>{flightDuration.split(' ')[0]}</span>
            </div>
            <div style={{ width: '50px', textAlign: 'left' }}>
              <span>
                <UnitWrapper
                  unit={flightDuration.split(' ')[1]}
                  inheritColour={activeSOS}
                  type={undefined}
                />
              </span>
            </div>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};
export default CollapsedFlightDetailItem;
